import { useState, useEffect } from "react";
import { pipelineAPI, projectsAPI } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Calendar } from "../components/ui/calendar";
import { toast } from "sonner";
import { GripVertical, Calendar as CalendarIcon, Sun, Battery, Archive, ArchiveRestore } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";

const stageColors = {
  neu: "bg-blue-500",
  angebot_erstellt: "bg-yellow-500",
  angebot_angenommen: "bg-green-500",
  angebot_abgelehnt: "bg-red-500",
  planung: "bg-purple-500",
  montage_geplant: "bg-orange-500",
  in_montage: "bg-cyan-500",
  abgeschlossen: "bg-emerald-500",
  bezahlt: "bg-teal-500"
};

export default function PipelinePage() {
  const [stages, setStages] = useState([]);
  const [pipelineData, setPipelineData] = useState({});
  const [archivedProjects, setArchivedProjects] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [draggedProject, setDraggedProject] = useState(null);
  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [canEdit, setCanEdit] = useState(true);  // Whether user can edit pipeline

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [stagesRes, pipelineRes] = await Promise.all([
        pipelineAPI.getStages(),
        pipelineAPI.get()
      ]);
      setStages(stagesRes.data.stages);
      // New response format includes stages and can_edit flag
      setPipelineData(pipelineRes.data.stages || pipelineRes.data);
      setCanEdit(pipelineRes.data.can_edit !== false);
      
      // Load archived projects if toggle is on
      if (showArchived) {
        loadArchivedProjects();
      }
    } catch (error) {
      console.error("Failed to load pipeline:", error);
      toast.error("Fehler beim Laden der Pipeline");
    } finally {
      setLoading(false);
    }
  };

  const loadArchivedProjects = async () => {
    try {
      const response = await projectsAPI.list(null, true);
      const archived = response.data.filter(p => p.archived);
      setArchivedProjects(archived);
    } catch (error) {
      console.error("Failed to load archived projects:", error);
    }
  };

  useEffect(() => {
    if (showArchived) {
      loadArchivedProjects();
    }
  }, [showArchived]);

  const handleArchiveProject = async (projectId) => {
    try {
      await projectsAPI.archive(projectId);
      toast.success("Projekt archiviert");
      loadData();
    } catch (error) {
      toast.error("Fehler beim Archivieren");
    }
  };

  const handleUnarchiveProject = async (projectId) => {
    try {
      await projectsAPI.unarchive(projectId);
      toast.success("Projekt wiederhergestellt");
      loadData();
      loadArchivedProjects();
    } catch (error) {
      toast.error("Fehler beim Wiederherstellen");
    }
  };

  const handleDragStart = (e, project, fromStage) => {
    if (!canEdit) {
      e.preventDefault();
      return;
    }
    setDraggedProject({ project, fromStage });
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    if (!canEdit) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (e, toStage) => {
    e.preventDefault();
    
    if (!canEdit) {
      setDraggedProject(null);
      return;
    }
    
    if (!draggedProject || draggedProject.fromStage === toStage) {
      setDraggedProject(null);
      return;
    }

    // Check if moving to montage_geplant requires a date
    if (toStage === "montage_geplant") {
      setSelectedProject({ ...draggedProject, toStage });
      setDateDialogOpen(true);
      setDraggedProject(null);
      return;
    }

    try {
      await pipelineAPI.move(draggedProject.project.id, toStage);
      toast.success("Projekt verschoben");
      loadData();
    } catch (error) {
      console.error("Failed to move project:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Verschieben");
    }
    
    setDraggedProject(null);
  };

  const handleSetInstallationDate = async () => {
    if (!selectedProject || !selectedDate) return;

    try {
      // First update the installation date
      await projectsAPI.updateStatus(selectedProject.project.id, {
        status: selectedProject.toStage,
        installation_date: selectedDate.toISOString()
      });
      
      toast.success("Montagetermin eingetragen");
      setDateDialogOpen(false);
      setSelectedProject(null);
      setSelectedDate(null);
      loadData();
    } catch (error) {
      console.error("Failed to set date:", error);
      toast.error("Fehler beim Speichern des Termins");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="pipeline-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Pipeline</h1>
          <p className="text-muted-foreground">
            {canEdit 
              ? "Verwalten Sie den Projektstatus per Drag & Drop" 
              : "Übersicht Ihrer Projekte (nur Ansicht)"}
          </p>
        </div>
        
        {/* Archive Toggle - only for admins */}
        {canEdit && (
          <div className="flex items-center space-x-2">
            <Switch
              id="show-archived"
              checked={showArchived}
              onCheckedChange={setShowArchived}
            />
            <Label htmlFor="show-archived" className="text-sm text-muted-foreground">
              Archivierte Projekte ({archivedProjects.length})
            </Label>
          </div>
        )}
      </div>

      {/* Info Banner for Vertriebler */}
      {!canEdit && (
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 text-sm text-blue-600">
          Als Vertriebler können Sie die Pipeline nur ansehen. Status-Änderungen werden vom Admin vorgenommen.
        </div>
      )}

      {/* Archived Projects Section */}
      {showArchived && archivedProjects.length > 0 && (
        <Card className="border-dashed">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Archive className="h-5 w-5 text-muted-foreground" />
              Archivierte Projekte
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {archivedProjects.map((project) => (
                <Card key={project.id} className="bg-muted/30">
                  <CardContent className="p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-sm">{project.customer_name}</p>
                        <p className="text-xs text-muted-foreground font-mono">{project.project_number}</p>
                        <Badge variant="outline" className="mt-2 text-xs">
                          {stages.find(s => s.id === project.status)?.name || project.status}
                        </Badge>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUnarchiveProject(project.id)}
                        title="Wiederherstellen"
                      >
                        <ArchiveRestore className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Kanban Board */}
      <div className="kanban-container">
        {stages.map((stage) => {
          const projects = pipelineData[stage.id] || [];
          
          return (
            <div
              key={stage.id}
              className="kanban-column"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, stage.id)}
            >
              <Card className="h-full">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <div className={`h-3 w-3 rounded-full ${stageColors[stage.id]}`} />
                      {stage.name}
                    </CardTitle>
                    <Badge variant="secondary">{projects.length}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <ScrollArea className="h-[calc(100vh-280px)]">
                    <div className="space-y-3 pr-4">
                      {projects.map((project) => (
                        <Card
                          key={project.id}
                          draggable={canEdit}
                          onDragStart={(e) => handleDragStart(e, project, stage.id)}
                          className={`card-hover border-l-4 ${canEdit ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'}`}
                          style={{ borderLeftColor: `var(--${stageColors[stage.id]?.replace('bg-', '')})` }}
                          data-testid={`pipeline-card-${project.id}`}
                        >
                          <CardContent className="p-3">
                            <div className="flex items-start gap-2">
                              {canEdit && <GripVertical className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />}
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">
                                  {project.customer_name}
                                </p>
                                <p className="text-xs text-muted-foreground font-mono">
                                  {project.project_number}
                                </p>
                                
                                {(project.kwp || project.storage_size) && (
                                  <div className="flex gap-2 mt-2">
                                    {project.kwp && (
                                      <div className="flex items-center gap-1 text-xs">
                                        <Sun className="h-3 w-3 text-accent" />
                                        {typeof project.kwp === 'number' ? project.kwp.toFixed(2) : project.kwp} kWp
                                      </div>
                                    )}
                                    {project.storage_size && (
                                      <div className="flex items-center gap-1 text-xs">
                                        <Battery className="h-3 w-3 text-accent" />
                                        {project.storage_size} kWh
                                      </div>
                                    )}
                                  </div>
                                )}
                                
                                {project.installation_date && (
                                  <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                                    <CalendarIcon className="h-3 w-3" />
                                    {format(new Date(project.installation_date), "dd.MM.yyyy", { locale: de })}
                                  </div>
                                )}
                                
                                {/* Archive button - available for all projects when user can edit */}
                                {canEdit && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="mt-2 h-7 text-xs text-muted-foreground hover:text-destructive"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (window.confirm(`Projekt "${project.customer_name}" wirklich archivieren?`)) {
                                        handleArchiveProject(project.id);
                                      }
                                    }}
                                    data-testid={`archive-project-${project.id}`}
                                  >
                                    <Archive className="h-3 w-3 mr-1" />
                                    Archivieren
                                  </Button>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                      
                      {projects.length === 0 && (
                        <div className="text-center py-8 text-sm text-muted-foreground">
                          Keine Projekte
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          );
        })}
      </div>

      {/* Installation Date Dialog */}
      <Dialog open={dateDialogOpen} onOpenChange={setDateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Montagetermin festlegen</DialogTitle>
            <DialogDescription>
              Wählen Sie den geplanten Montagetermin für dieses Projekt.
            </DialogDescription>
          </DialogHeader>

          <div className="flex justify-center py-4">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              locale={de}
              disabled={(date) => date < new Date()}
              className="rounded-md border"
            />
          </div>

          {selectedDate && (
            <p className="text-center text-sm">
              Gewählter Termin: <strong>{format(selectedDate, "dd. MMMM yyyy", { locale: de })}</strong>
            </p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDateDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSetInstallationDate} disabled={!selectedDate}>
              Termin speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
